import { computed, onMounted, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import Swal from "sweetalert2";

import reportService, { type ProfitLossReportItem } from "@/services/report";
import warehouseService, { type Warehouse } from "@/services/warehouse";

type ProfitLossFilters = {
  warehouse_id: number | null;
  year: number | "lifetime";
};

const currentYear = new Date().getFullYear();

const createInitialFilters = (): ProfitLossFilters => ({
  warehouse_id: null,
  year: currentYear,
});

export function useProfitLossReport() {
  const { t } = useI18n();
  const warehouses = ref<Warehouse[]>([]);
  const loading = ref(false);
  const exporting = ref(false);
  const filters = reactive<ProfitLossFilters>(createInitialFilters());
  const rows = ref<ProfitLossReportItem[]>([]);
  const includesIncome = ref(true);
  const i18nScope = "profit_loss_report_page";
  const pageTitle = `${i18nScope}.title`;

  const years = computed(() => {
    const list: number[] = [];
    for (let year = currentYear; year >= currentYear - 5; year -= 1) {
      list.push(year);
    }
    return list;
  });

  const summary = computed(() => ({
    sales: rows.value.reduce((sum, item) => sum + Number(item.sales || 0), 0),
    sales_returns: rows.value.reduce((sum, item) => sum + Number(item.sales_returns || 0), 0),
    income: rows.value.reduce((sum, item) => sum + Number(item.income || 0), 0),
    purchases: rows.value.reduce((sum, item) => sum + Number(item.purchases || 0), 0),
    expenses: rows.value.reduce((sum, item) => sum + Number(item.expenses || 0), 0),
    net_profit: rows.value.reduce((sum, item) => sum + Number(item.net_profit || 0), 0),
  }));

  const fetchWarehouses = async () => {
    try {
      warehouses.value = await warehouseService.getWarehouses();
    } catch (error) {
      console.error("Error fetching profit and loss warehouses:", error);
    }
  };

  const fetchProfitLoss = async () => {
    loading.value = true;
    try {
      const response = await reportService.getProfitLossReport({
        year: filters.year,
        warehouse_id: filters.warehouse_id || undefined,
      });

      rows.value = response.data ?? [];
      includesIncome.value = Boolean(response.includes_income);
    } catch (error) {
      console.error("Error fetching profit and loss report:", error);
      Swal.fire({
        icon: "error",
        title: t(`${i18nScope}.load_failed`),
        text: t(`${i18nScope}.unable_to_load_report`),
      });
    } finally {
      loading.value = false;
    }
  };

  const exportProfitLoss = async () => {
    exporting.value = true;
    try {
      const response = await reportService.exportProfitLossReport({
        year: filters.year,
        warehouse_id: filters.warehouse_id || undefined,
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `profit_loss_${String(filters.year)}_${new Date().getTime()}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting profit and loss report:", error);
      Swal.fire({
        icon: "error",
        title: t(`${i18nScope}.export_failed`),
        text: t(`${i18nScope}.unable_to_export_report`),
      });
    } finally {
      exporting.value = false;
    }
  };

  const changeYear = (delta: number) => {
    if (filters.year === "lifetime") return;
    const nextYear = Number(filters.year) + delta;
    if (nextYear <= currentYear) {
      filters.year = nextYear;
    }
  };

  watch(
    () => [filters.year, filters.warehouse_id],
    () => {
      fetchProfitLoss();
    }
  );

  onMounted(async () => {
    await fetchWarehouses();
    await fetchProfitLoss();
  });

  return {
    pageTitle,
    i18nScope,
    warehouses,
    rows,
    loading,
    exporting,
    filters,
    years,
    currentYear,
    summary,
    includesIncome,
    changeYear,
    exportProfitLoss,
  };
}
